import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../redux/store/store';
import { deleteEvent, selectDeleteEventStatus } from '../../../redux/slices/editEventSlice';
import { fetchEvents, removeEventById } from '../../../redux/slices/eventsSlice';
import { EventData } from '../../../util/types';
import { parseTimeDateFromString } from '../../../util/utils';
import ConfirmationDialog from '../../general/ConfirmationDialog';

interface MediaCardType {
  event: EventData;
  clubId: string;
  setEventSelected: (t: string) => void;
}

export default function MasterEventCard({
  event,
  clubId,
  setEventSelected,
}: MediaCardType) {
  const dispatch: AppDispatch = useDispatch();
  const reqStatus = useSelector(selectDeleteEventStatus);
  const [openDialog, setOpenDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDeleteEvent = () => {
    // Your delete event logic here
    dispatch(deleteEvent({
      eventId: event.eventId ?? "", clubId: clubId,
      callback: () => {
        dispatch(removeEventById(event.eventId ?? ""));
        dispatch(fetchEvents({ clubId: clubId ?? "", forceRefresh: true }));
      }
    }))
    console.log('Event deleted');
    setOpenDialog(false);
  };

  const handleCopyEventId = async () => {
    if (!event.eventId) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(event.eventId);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = event.eventId;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy eventId', error);
    }
  };

  return (
    <Card sx={{ margin: 4 }}>
      <CardMedia sx={{ height: 140 }} image={event.eventImage} />
      <CardContent>
        <Typography noWrap gutterBottom variant="h5" component="div">
          {event.eventName}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {"StartTime: " + parseTimeDateFromString(event.startTime)}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {"EndTime: " + parseTimeDateFromString(event.endTime)}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {"CreatedAT: " + parseTimeDateFromString(event.createdAt ?? "")}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
          >
            {"EventId: " + (event.eventId ?? "-")}
          </Typography>
          {event.eventId ? (
            <Tooltip title={copied ? 'Kopiert' : 'Kopieren'}>
              <IconButton
                size="small"
                aria-label="Copy event ID"
                onClick={handleCopyEventId}
              >
                {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </CardContent>
      <CardActions>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => setEventSelected(event.eventId ?? "")}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Bearbeiten
        </Button>
        <Button
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => setOpenDialog(true)} // Open confirmation dialog on click
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Löschen
        </Button>
      </CardActions>
      <ConfirmationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleDeleteEvent}
        title="Confirmation"
        message="Willst du dieses Event wirklich löschen?"
      />
    </Card>
  );
}
